import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';

import type { Question, QuestionProperties } from '@api/instant_survey/types';
import { QUESTION_TYPES } from '@api/instant_survey';
import { Button, ErrorText, Fieldset, Input, Layout, OverlayPopover, Pill } from '@utilities';

import { VALIDATION_MESSAGES } from '../../../../utilities/constants';

import styles from './_index.module.scss';

interface ValuesBetweenProps {
  question: Question;
  updateQuestionProperties: (properties: Partial<QuestionProperties>) => void;
}

const ValuesBetween = ({ question, updateQuestionProperties }: ValuesBetweenProps) => {
  const isVanWestendorp = question.type === QUESTION_TYPES.VAN_WESTENDORP.value;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [propertiesToApply, setPropertiesToApply] = useState<Partial<QuestionProperties>>({
    ...question.properties,
  });

  const pillRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLElement>(null); // required to allow inputs inside the popover to be focused

  useEffect(() => {
    if (isPopoverOpen) {
      setPropertiesToApply({ ...question.properties });
    }
  }, [isPopoverOpen]);

  const handleApply = useCallback(() => {
    updateQuestionProperties({ ...propertiesToApply });
    setIsPopoverOpen(false);
  }, [propertiesToApply, updateQuestionProperties]);

  const handleChange = useCallback(
    (key: 'maxNumber' | 'minNumber') => (value: number | string) => {
      const parsed = parseFloat(value as string);
      setPropertiesToApply((prev) => ({ ...prev, [key]: isNaN(parsed) ? undefined : parsed }));
    },
    []
  );

  const getErrorMessage = () => {
    if (propertiesToApply.minNumber !== undefined) {
      if (isVanWestendorp && propertiesToApply.minNumber <= 0)
        return VALIDATION_MESSAGES.VW_MIN_PRICE_ERROR;
      if (propertiesToApply.minNumber < 0) return VALIDATION_MESSAGES.MIN_VALUE_NEGATIVE_ERROR;
    }

    if (propertiesToApply.maxNumber === undefined || propertiesToApply.minNumber === undefined) {
      return null;
    }

    if (propertiesToApply.maxNumber <= propertiesToApply.minNumber)
      return VALIDATION_MESSAGES.MAX_VALUE_MIN_VALUE_ERROR;

    if (
      question.properties.maxTotal !== undefined &&
      propertiesToApply.maxNumber > question.properties.maxTotal
    )
      return VALIDATION_MESSAGES.MAX_VALUE_MAX_TOTAL_ERROR;

    if (question.properties.forcePercent && propertiesToApply.maxNumber > 100)
      return VALIDATION_MESSAGES.MAX_PERCENTAGE_ERROR;

    return null;
  };

  const errorMessage = getErrorMessage();
  const isApplyDisabled =
    propertiesToApply.minNumber === undefined ||
    propertiesToApply.maxNumber === undefined ||
    isNaN(propertiesToApply.minNumber) ||
    isNaN(propertiesToApply.maxNumber) ||
    propertiesToApply.minNumber < 0 ||
    (isVanWestendorp && propertiesToApply.minNumber <= 0) ||
    propertiesToApply.maxNumber <= propertiesToApply.minNumber ||
    (question.properties.maxTotal !== undefined &&
      propertiesToApply.maxNumber > question.properties.maxTotal) ||
    (question.properties.forcePercent && propertiesToApply.maxNumber > 100) ||
    isEqual(question.properties, propertiesToApply);

  return (
    <>
      <Layout.Flex className={styles['values-between']} gap="x-small">
        <span ref={popoverRef}>Allow Values Between</span>
        <Pill
          buttonRef={pillRef}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          value={
            question.properties.minNumber !== undefined &&
            question.properties.maxNumber !== undefined
              ? `${question.properties.minNumber} and ${question.properties.maxNumber}`
              : 'Not set'
          }
        />
      </Layout.Flex>
      <OverlayPopover
        className={styles['values-between-popover']}
        container={popoverRef.current!}
        hasArrow
        isActive={isPopoverOpen}
        onHide={() => setIsPopoverOpen(false)}
        target={pillRef.current}
      >
        <>
          <Fieldset legend="Allow Values Between">
            <Layout.Flex className={styles['values-between-row']} gap="small">
              <Input
                id="min-number-input"
                min={0}
                onChange={handleChange('minNumber')}
                type="number"
                value={propertiesToApply.minNumber ?? ''}
              />
              <Input
                id="max-number-input"
                max={question.properties.maxTotal}
                min={(propertiesToApply.minNumber ?? 0) + 1}
                onChange={handleChange('maxNumber')}
                type="number"
                value={propertiesToApply.maxNumber ?? ''}
              />
            </Layout.Flex>
            {errorMessage && (
              <ErrorText className={styles['values-between-error']} text={errorMessage} />
            )}
          </Fieldset>
          <Button isDisabled={isApplyDisabled} onClick={handleApply} text="Apply" />
        </>
      </OverlayPopover>
    </>
  );
};

export default ValuesBetween;
